import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";

const BAND_TONE = {
  Critical: "bg-red-100 text-red-800 border-red-200",
  High: "bg-orange-100 text-orange-800 border-orange-200",
  Medium: "bg-amber-100 text-amber-800 border-amber-200",
  Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Not known": "bg-gray-100 text-gray-600 border-gray-200",
};

const DIMENSION_LABEL = {
  findings: "Open findings",
  privilege: "Privilege",
  posture: "Posture weaknesses",
  surface: "What it can reach",
  hygiene: "Dormancy",
  proximity: "Attack-path proximity",
};

export const bandTone = (band) => BAND_TONE[band] || BAND_TONE["Not known"];

/**
 * One problem, stated. The four lines are the whole point of the block: before it this drawer
 * printed true facts — "holds a directory role", "reaches 1 app with a risky scope" — and left the
 * reader to work out that those two facts together were the problem, which nobody did.
 *
 * "Fixed when" is not a promise that we will close it for them: it says the entry disappears when
 * the source stops reporting the fact, so nobody has to mark anything done.
 */
export function Verdict({ verdict }) {
  return (
    <li className="border border-gray-200 rounded-md p-2 bg-white" data-testid={`verdict-${verdict.key}`}>
      <div className="flex items-start gap-2">
        <span className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold flex-shrink-0 ${bandTone(verdict.severity)}`}>
          {verdict.severity}
        </span>
        <span className="text-xs font-semibold text-gray-900">{verdict.title}</span>
      </div>
      <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">{verdict.why}</p>
      <p className="text-[11px] text-gray-800 mt-1 leading-relaxed">
        <span className="font-semibold">What to do: </span>{verdict.action}
      </p>
      <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
        <span className="font-semibold">Fixed when: </span>{verdict.proof}
      </p>
      {verdict.controlTitle && (
        <p className="text-[11px] text-gray-400 mt-1">
          Evidence for “{verdict.controlTitle}” — the control’s own status still comes from its assessment.
        </p>
      )}
    </li>
  );
}

const money = (value) =>
  typeof value !== "number" || value <= 0 ? null
    : value >= 1_000_000 ? `$${(value / 1_000_000).toFixed(1)}M`
    : value >= 1_000 ? `$${Math.round(value / 1_000)}K`
    : `$${Math.round(value)}`;

/**
 * Fork 2: how exposed this identity is, and — just as importantly — how much of the model could be
 * measured on this tenant.
 *
 * A dimension whose evidence was never observed is shown as "not known" and is NOT part of the
 * score; the score is a weighted mean over what was observed, and the coverage line says so. A
 * reader must never mistake "we could not look" for "this account is fine", which is the same
 * mistake as counting an unassessed control as a pass.
 */
export default function IdentityExposure({ id }) {
  const apiCall = useApi();
  const [data, setData] = useState(null);   // null = loading
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null); setFailed(false);
    apiCall("GET", `/defender-identity/identities/${id}/exposure`)
      .then(r => { if (!cancelled) (r && Array.isArray(r.dimensions) ? setData(r) : setFailed(true)); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [apiCall, id]);

  if (failed) {
    return (
      <>
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">Exposure</div>
        <div className="text-xs text-gray-500 py-1.5" data-testid="exposure-unavailable">
          The exposure score could not be read for this identity.
        </div>
      </>
    );
  }

  const observedPct = data ? Math.round((data.observedWeight ?? 0) * 100) : 0;
  const scored = data?.dimensions?.filter(d => d.weight > 0) ?? [];

  const verdicts = data?.verdicts ?? [];
  const verdictsNotKnown = data?.verdictsNotKnown ?? [];

  return (
    <>
      {data && (
        <>
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">
            What is wrong
            <span className="normal-case tracking-normal"
              title="How serious each problem is. The account's exposure score below is a separate, blended measure.">
              {" "}· severity of the problem
            </span>
          </div>
          {verdicts.length > 0 ? (
            <ul className="space-y-1.5" data-testid="identity-verdicts">
              {verdicts.map(v => <Verdict key={v.key} verdict={v} />)}
            </ul>
          ) : (
            <div className="text-xs text-gray-500 py-1" data-testid="identity-verdicts-none">
              Nothing we check for was found on this account.
            </div>
          )}
          {/* Silence is not a pass: an unread source is named, so an empty block above cannot be
              mistaken for a clean account. */}
          {verdictsNotKnown.length > 0 && (
            <div className="text-[11px] text-amber-700 mt-1" data-testid="identity-verdicts-not-known">
              Not checked, because the evidence was not read: {verdictsNotKnown.join(", ")}.
            </div>
          )}
        </>
      )}

      <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">
        Exposure
        {data && (
          <span className="normal-case tracking-normal"
            title="A 0–100 blend of findings, privilege, posture, reach and hygiene. One severe problem above is only part of it, so the band can be lower than the worst problem.">
            {" "}· account score, blended
          </span>
        )}
      </div>
      {data === null && <div className="text-xs text-gray-400 py-1.5">Loading…</div>}

      {data && (
        <div data-testid="identity-exposure">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-gray-900 tabular-nums">
              {typeof data.score === "number" ? data.score : "—"}
            </span>
            <span className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold ${bandTone(data.band)}`}>
              {data.band}
            </span>
            {money(data.monetaryImpact) && (
              <span className="text-xs text-gray-600" title="Sum of the FAIR estimates on this identity's open findings">
                {money(data.monetaryImpact)} exposure
                {data.risksWithoutEstimate > 0 ? ` · ${data.risksWithoutEstimate} finding(s) without an estimate` : ""}
              </span>
            )}
          </div>

          {/* What the work is worth. Deliberately not "drops to 0": privilege and dormancy are not
              fixed by closing a finding, and promising otherwise would misdescribe the remediation. */}
          {typeof data.score === "number" && typeof data.resolvedScore === "number" && data.resolvedScore < data.score && (
            <div className="text-[11px] text-emerald-700 mt-0.5" data-testid="exposure-resolved">
              If its open findings are resolved: {data.score} → {data.resolvedScore}
              {money(data.monetaryImpact) && ` · ${money(data.monetaryImpact)} removed`}
            </div>
          )}

          {/* The coverage line is not decoration: it is what stops the number being read as complete. */}
          <div className="text-[11px] text-gray-500 mt-0.5">
            Measured on {observedPct}% of the model
            {data.notKnown?.length > 0 && ` · not known: ${data.notKnown.map(k => DIMENSION_LABEL[k] || k).join(", ")}`}
          </div>

          <ul className="mt-2 space-y-1" data-testid="exposure-dimensions">
            {scored.map(d => (
              <li key={d.key} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-700">{DIMENSION_LABEL[d.key] || d.key}</span>
                  <span className={d.observed ? "text-gray-800 tabular-nums" : "text-gray-400"}>
                    {d.observed ? Math.round(d.score) : "not known"}
                    <span className="text-gray-400"> · weight {Math.round(d.weight * 100)}%</span>
                  </span>
                </div>
                <div className="h-1 rounded bg-gray-100 overflow-hidden mt-0.5">
                  <div
                    className={`h-full ${d.observed ? "bg-indigo-400" : "bg-gray-200"}`}
                    style={{ width: `${d.observed ? Math.min(100, d.score) : 100}%` }}
                  />
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">{d.explanation}</div>
              </li>
            ))}
          </ul>

          {(data.trajectory45 > 0 || data.trajectory90 > 0 || data.trajectory180 > 0) && (
            <div className="text-[11px] text-gray-500 mt-2" data-testid="exposure-trajectory">
              If nothing is remediated: {Math.round(data.trajectory45)} at 45 days ·{" "}
              {Math.round(data.trajectory90)} at 90 · {Math.round(data.trajectory180)} at 180.
            </div>
          )}
        </div>
      )}
    </>
  );
}
